import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, Check, Loader2, Clock, Sparkles, Zap, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { logError } from '../lib/errorLogger';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

const PremiumActiveCard = ({ planDetails }) => {
  const planName = planDetails?.plan_name || 'Premium';
  const amount = planDetails?.amount;
  const interval = planDetails?.interval;
  const renewalDate = planDetails?.renewal_date;
  const formattedRenewal = renewalDate
    ? new Date(renewalDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Card className="card-elevated border-0 border-l-4 border-l-[#4ADE80]">
      <CardContent className="p-8">
        <div className="text-center mb-6">
          <Crown className="w-12 h-12 text-[#4ADE80] mx-auto mb-4" />
          <h3 className="font-heading text-2xl font-bold text-[#1A1514] mb-1">Premium Active</h3>
          <p className="text-[#8C7A76] text-sm">Full access to all features</p>
        </div>
        <div className="bg-[#FFF2EC] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#8C7A76]">Plan</span>
            <span className="text-sm font-semibold text-[#1A1514]" data-testid="active-plan-name">{planName}</span>
          </div>
          {amount > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#8C7A76]">Price</span>
              <span className="text-sm font-semibold text-[#1A1514]">${amount}/{interval}</span>
            </div>
          )}
          {formattedRenewal && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#8C7A76]">Renews on</span>
              <span className="text-sm font-semibold text-[#1A1514]" data-testid="renewal-date">{formattedRenewal}</span>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {['Unlimited AI Coach', 'Unlimited Selfies', 'Food Trigger Heatmap', 'Product Recommendations'].map((f) => (
            <div key={f} className="flex items-center gap-2 text-xs text-[#FF6B4A]">
              <Check className="w-3.5 h-3.5 flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const TrialBanner = ({ trialStatus, onUpgrade }) => {
  if (!trialStatus) return null;

  // Trial active — show countdown
  if (trialStatus.is_trial) {
    const daysLeft = trialStatus.days_remaining;
    const urgent = daysLeft <= 2;
    return (
      <div className={`rounded-xl p-4 flex items-center gap-4 ${urgent ? 'bg-[#A388EE]/10 border border-[#A388EE]/30' : 'bg-[#4ADE80]/10 border border-[#4ADE80]/30'}`} data-testid="trial-banner">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${urgent ? 'bg-[#A388EE]/20' : 'bg-[#4ADE80]/20'}`}>
          <Clock className={`w-6 h-6 ${urgent ? 'text-[#A388EE]' : 'text-[#FF6B4A]'}`} />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#1A1514] text-sm">
            {urgent ? `Only ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left!` : `${daysLeft} days left in your free trial`}
          </p>
          <p className="text-xs text-[#8C7A76] mt-0.5">
            {urgent ? "Subscribe now to keep your Premium features" : "Enjoying Premium? Subscribe to keep access after your trial ends"}
          </p>
        </div>
        <Button onClick={onUpgrade} className={`rounded-full px-4 text-sm font-semibold ${urgent ? 'bg-[#A388EE] hover:bg-[#b5664a]' : 'bg-[#FF6B4A] hover:bg-[#E8552F]'}`} data-testid="trial-upgrade-btn">
          Subscribe Now
        </Button>
      </div>
    );
  }

  // Trial expired — show conversion prompt
  if (trialStatus.trial_expired) {
    return (
      <div className="rounded-xl p-5 bg-gradient-to-r from-[#A388EE]/10 to-[#A388EE]/5 border border-[#A388EE]/20 text-center" data-testid="trial-expired-banner">
        <Clock className="w-8 h-8 text-[#A388EE] mx-auto mb-2" />
        <p className="font-bold text-[#1A1514]">Your free trial has ended</p>
        <p className="text-sm text-[#8C7A76] mt-1 mb-4">Don't lose your progress! Subscribe to keep Premium features.</p>
        <Button onClick={onUpgrade} className="bg-[#A388EE] hover:bg-[#b5664a] rounded-full px-6 font-semibold" data-testid="trial-expired-subscribe">
          Subscribe — Keep Premium
        </Button>
      </div>
    );
  }

  return null;
};

const FreeTrialCard = ({ onStartTrial, loading, trialUsed }) => {
  if (trialUsed) return null;

  return (
    <Card className="border-0 shadow-lg bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] text-white overflow-hidden relative" data-testid="free-trial-card">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
      <CardContent className="p-8 relative">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#4ADE80]" />
          <span className="text-sm font-semibold text-[#4ADE80] uppercase tracking-wider">Limited Offer</span>
        </div>
        <h3 className="font-heading text-2xl font-bold mb-2">Try Premium Free for 7 Days</h3>
        <p className="text-white/70 text-sm mb-6">Experience everything InnerGlow has to offer. No credit card required. Cancel anytime.</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            'Unlimited AI Skin Coach',
            'Unlimited Selfie Tracking',
            'Food Trigger Heatmap',
            'Personalized Products',
          ].map((f) => (
            <div key={f} className="flex items-center gap-2 text-xs text-white/80">
              <Check className="w-3.5 h-3.5 text-[#4ADE80] flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
        <Button
          onClick={onStartTrial}
          disabled={loading}
          className="w-full bg-white text-[#FF6B4A] hover:bg-white/90 rounded-full py-5 font-bold text-base"
          data-testid="start-trial-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-5 h-5 mr-2" />}
          Start Free Trial
        </Button>
        <p className="text-center text-white/50 text-xs mt-3 flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" /> No credit card needed. Automatically reverts to Free after 7 days.
        </p>
      </CardContent>
    </Card>
  );
};

const PaymentStatusBanner = ({ status }) => {
  if (status === 'paid') {
    return (
      <div className="bg-[#4ADE80]/20 border border-[#4ADE80] rounded-xl p-4 text-center" data-testid="payment-success">
        <Check className="w-6 h-6 text-[#FF6B4A] mx-auto mb-2" />
        <p className="font-semibold text-[#FF6B4A]">Payment successful! Welcome to Premium.</p>
      </div>
    );
  }
  if (status === 'processing') {
    return (
      <div className="bg-[#FFF2EC] rounded-xl p-4 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B4A] mx-auto mb-2" />
        <p className="text-[#8C7A76]">Verifying your payment...</p>
      </div>
    );
  }
  return null;
};

const PlanCard = ({ plan, onCheckout, checkingOut, isPopular }) => (
  <Card className={`card-elevated border-0 ${isPopular ? 'ring-2 ring-[#4ADE80] relative' : ''}`}>
    {isPopular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4ADE80] text-white text-xs font-bold px-4 py-1 rounded-full">
        Most Popular
      </div>
    )}
    <CardHeader className="pt-8">
      <CardTitle className="text-[#1A1514]">{plan.name}</CardTitle>
      <div className="mt-2">
        <span className="font-heading text-4xl font-bold text-[#1A1514]">${plan.amount}</span>
        <span className="text-[#8C7A76]">/{plan.interval}</span>
      </div>
      {plan.plan_id === 'premium_yearly' && (
        <p className="text-xs text-[#4ADE80] font-semibold mt-1">Save $40/year vs monthly</p>
      )}
    </CardHeader>
    <CardContent>
      <ul className="space-y-3 mb-6">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-center gap-2 text-sm text-[#1A1514]">
            <Check className="w-4 h-4 text-[#4ADE80] flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      <Button className="w-full bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full"
        onClick={() => onCheckout(plan.plan_id)} disabled={checkingOut !== null}
        data-testid={`checkout-${plan.plan_id}`}>
        {checkingOut === plan.plan_id ? (
          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
        ) : (
          'Subscribe Now'
        )}
      </Button>
    </CardContent>
  </Card>
);

const SubscriptionSection = ({ subscription }) => {
  const [plans, setPlans] = useState([]);
  const [checkingOut, setCheckingOut] = useState(null);
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const { checkAuth } = useAuth();

  useEffect(() => {
    axios.get(`${API}/subscription/plans`)
      .then((res) => setPlans(res.data))
      .catch((err) => logError('SubscriptionSection', 'Failed to fetch plans', { error: err?.message }));
  }, []);

  // Fetch plan details for premium users
  useEffect(() => {
    if (subscription === 'premium') {
      axios.get(`${API}/subscription/status`, { withCredentials: true })
        .then((res) => setPlanDetails(res.data))
        .catch(() => {});
    }
  }, [subscription]);

  // Fetch trial status
  useEffect(() => {
    axios.get(`${API}/trial/status`, { withCredentials: true })
      .then((res) => setTrialStatus(res.data))
      .catch(() => {});
  }, [subscription]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        toast.success('Payment successful! Premium unlocked.');
        await checkAuth();
        return;
      }
      if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), POLL_INTERVAL_MS);
    } catch {
      setPaymentStatus('error');
    }
  }, [checkAuth]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPaymentStatus('processing');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleCheckout = async (planId) => {
    setCheckingOut(planId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId, origin_url: origin
      }, { withCredentials: true });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      logError('SubscriptionSection', 'Checkout creation failed', { error: err?.message, planId });
      toast.error('Checkout failed. Please try again.');
    } finally {
      setCheckingOut(null);
    }
  };

  const handleStartTrial = async () => {
    setTrialLoading(true);
    try {
      const res = await axios.post(`${API}/trial/start`, {}, { withCredentials: true });
      toast.success(res.data.message);
      await checkAuth();
      setTrialStatus({ is_trial: true, days_remaining: 7, trial_used: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start trial');
    } finally {
      setTrialLoading(false);
    }
  };

  // Premium active (paid or trial)
  if (subscription === 'premium' && planDetails?.plan_id !== 'free_trial') {
    return <PremiumActiveCard planDetails={planDetails} />;
  }

  // On trial — show trial banner + subscribe options
  const isTrial = trialStatus?.is_trial || planDetails?.plan_id === 'free_trial';
  const scrollToPlans = () => document.getElementById('pricing-plans')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="space-y-6">
      <PaymentStatusBanner status={paymentStatus} />

      {/* Trial banner (active or expired) */}
      {(isTrial || trialStatus?.trial_expired) && (
        <TrialBanner trialStatus={trialStatus} onUpgrade={scrollToPlans} />
      )}

      {/* Free trial card — only for users who haven't tried yet */}
      {!isTrial && subscription !== 'premium' && (
        <FreeTrialCard onStartTrial={handleStartTrial} loading={trialLoading} trialUsed={trialStatus?.trial_used} />
      )}

      <div className="text-center mb-4" id="pricing-plans">
        <Crown className="w-10 h-10 text-[#A388EE] mx-auto mb-3" />
        <h3 className="font-heading text-2xl font-bold text-[#1A1514]">
          {isTrial ? 'Keep Your Premium Access' : 'Unlock Premium'}
        </h3>
        <p className="text-[#8C7A76] mt-2">
          {isTrial
            ? 'Subscribe before your trial ends to keep all features'
            : 'Get AI-powered insights and unlimited features'
          }
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <PlanCard key={plan.plan_id} plan={plan} onCheckout={handleCheckout} checkingOut={checkingOut} isPopular={plan.plan_id === 'premium_yearly'} />
        ))}
      </div>
    </div>
  );
};

export default SubscriptionSection;
